/* Central Creativity -- My List.
 *
 * WHAT THIS IS. A save-for-later list that needs no account, no app and no
 * monthly fee. A teacher builds a list of kits, then sends it to whoever
 * signs the check. The sending is the point: the list itself is only useful
 * because it can leave the browser as a link, a printout or a quote request.
 *
 * WHERE IT LIVES. localStorage, key "cc-my-list". That is NOT session
 * storage: it survives closing the tab, quitting the browser and restarting
 * the computer, and it is still there weeks later. What it does not survive
 * is a different device, a different browser, a private window, or the
 * visitor clearing their browsing data. The page says so in those words,
 * because a vague warning makes people distrust a feature that mostly works.
 *
 * WHAT IS STORED is only the product handle, a quantity and an optional
 * variant id -- never a title, price or image. Prices on this list are read
 * fresh from the store every time the page renders, because somebody prints
 * this and hands it to a purchasing office, and a stale price on that sheet
 * is worse than no list at all.
 *
 * WHY IT DECORATES AT RUNTIME. The save buttons are attached by this script
 * rather than baked into Concept's product-card.liquid (32KB) and
 * main-product.liquid (136KB). Re-uploading files that size to this store is
 * where silent truncation has bitten us before. One script tag in
 * layout/theme.liquid is the entire footprint, and deleting that line removes
 * the feature completely.
 *
 * EVERY ENTRY POINT FAILS SILENT. A missing container, a blocked
 * localStorage, a product that has since been deleted -- none of them may
 * throw, because this runs on every page of the storefront including the
 * cart and checkout approach.
 */
(function () {
  'use strict';

  var KEY = 'cc-my-list';
  var EV = 'cc-mylist:change';
  var MAX = 60;

  /* ---------------------------------------------------------------- store */

  function root() {
    try {
      var r = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
      return r || '/';
    } catch (e) {
      return '/';
    }
  }

  function currency() {
    try {
      return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    } catch (e) {
      return 'USD';
    }
  }

  function read() {
    var raw = null;
    try {
      raw = window.localStorage.getItem(KEY);
    } catch (e) {
      return [];
    }
    if (!raw) return [];
    var data = null;
    try {
      data = JSON.parse(raw);
    } catch (e) {
      return [];
    }
    var items = data && data.items;
    if (!items || !items.length) return [];
    var out = [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (!it || typeof it.h !== 'string' || !it.h) continue;
      var q = parseInt(it.q, 10);
      out.push({
        h: it.h,
        q: q > 0 ? Math.min(q, 999) : 1,
        id: it.id ? String(it.id) : null
      });
    }
    return out;
  }

  function write(items) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify({
        v: 1,
        items: items.slice(0, MAX),
        at: Date.now()
      }));
    } catch (e) {
      /* Private window, or the quota is full. The list stops persisting; the
         page keeps working for this visit. Nothing here may throw. */
    }
    try {
      document.dispatchEvent(new CustomEvent(EV));
    } catch (e) {
      /* no-op */
    }
  }

  function indexOf(items, handle) {
    for (var i = 0; i < items.length; i++) {
      if (items[i].h === handle) return i;
    }
    return -1;
  }

  function has(handle) {
    return indexOf(read(), handle) > -1;
  }

  function add(handle, variantId) {
    if (!handle) return;
    var items = read();
    var at = indexOf(items, handle);
    if (at > -1) {
      if (variantId) items[at].id = String(variantId);
    } else {
      items.push({ h: handle, q: 1, id: variantId ? String(variantId) : null });
    }
    write(items);
  }

  function drop(handle) {
    var items = read();
    var at = indexOf(items, handle);
    if (at > -1) {
      items.splice(at, 1);
      write(items);
    }
  }

  function toggle(handle, variantId) {
    if (has(handle)) {
      drop(handle);
      return false;
    }
    add(handle, variantId);
    return true;
  }

  function patch(handle, changes) {
    var items = read();
    var at = indexOf(items, handle);
    if (at < 0) return;
    for (var k in changes) {
      if (Object.prototype.hasOwnProperty.call(changes, k)) items[at][k] = changes[k];
    }
    write(items);
  }

  /* ----------------------------------------------------------- small util */

  function handleOf(href) {
    if (!href) return null;
    var s = String(href).split('?')[0].split('#')[0];
    var i = s.indexOf('/products/');
    if (i < 0) return null;
    var rest = s.slice(i + 10).split('/')[0];
    return rest || null;
  }

  /* Nothing that ships to this store may contain a backslash -- an upload
     doubled them once and printed a literal escape on a live page. The
     email body and the quote prefill both need real newlines, so the
     character is built instead of escaped. */
  var NL = String.fromCharCode(10);

  function money(cents) {
    var n = (parseInt(cents, 10) || 0) / 100;
    try {
      return n.toLocaleString(undefined, {
        style: 'currency',
        currency: currency(),
        minimumFractionDigits: 2
      });
    } catch (e) {
      return '$' + n.toFixed(2);
    }
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  var ICON =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" width="20" height="20">' +
    '<path d="M6 3.5h12a1.5 1.5 0 0 1 1.5 1.5v15.1a.9.9 0 0 1-1.38.76L12 16.9l-6.12 3.96A.9.9 0 0 1 4.5 20.1V5A1.5 1.5 0 0 1 6 3.5Z" ' +
    'fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/></svg>';

  var TICK =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" width="20" height="20">' +
    '<path d="M6 3.5h12a1.5 1.5 0 0 1 1.5 1.5v15.1a.9.9 0 0 1-1.38.76L12 16.9l-6.12 3.96A.9.9 0 0 1 4.5 20.1V5A1.5 1.5 0 0 1 6 3.5Z" ' +
    'fill="currentColor" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>' +
    '<path d="M8.6 10.1l2.4 2.4 4.4-4.4" fill="none" stroke="#fff" stroke-width="1.9" ' +
    'stroke-linecap="round" stroke-linejoin="round"/></svg>';

  /* ------------------------------------------------------- announcements */
  /* One polite live region for the whole feature. Saving is a state change
     with no visible confirmation elsewhere on the page, so a screen reader
     user would otherwise get nothing back from pressing the button. */

  var live = null;

  function say(message) {
    if (!live) {
      live = el('div', 'ccml-live');
      live.setAttribute('aria-live', 'polite');
      live.setAttribute('role', 'status');
      document.body.appendChild(live);
    }
    live.textContent = '';
    window.setTimeout(function () {
      live.textContent = message;
    }, 60);
  }

  /* --------------------------------------------------------- save buttons */

  function makeButton(handle, variant, wide) {
    var b = el('button', 'ccml-save' + (wide ? ' ccml-save--wide' : ''));
    b.type = 'button';
    b.setAttribute('data-ccml-save', handle);
    if (variant) b.setAttribute('data-ccml-variant', variant);
    b.appendChild(el('span', 'ccml-save__icon'));
    b.appendChild(el('span', 'ccml-save__label'));
    b.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      var live_variant = b.getAttribute('data-ccml-variant') || null;
      var now = toggle(handle, live_variant);
      say(now ? 'Saved to My List.' : 'Removed from My List.');
    });
    paint(b);
    return b;
  }

  function paint(b) {
    var handle = b.getAttribute('data-ccml-save');
    var on = has(handle);
    /* Only touch the DOM when the state actually changed. A repaint rewrites
       the icon, which is a childList mutation, which wakes the observer
       below, which repaints -- a loop that costs a frame every 120ms
       forever. Asked and answered here rather than in the observer, because
       any future caller gets the same protection. */
    var was = b.getAttribute('data-ccml-state');
    if (was === (on ? 'on' : 'off')) return;
    b.setAttribute('data-ccml-state', on ? 'on' : 'off');
    b.setAttribute('aria-pressed', on ? 'true' : 'false');
    b.classList.toggle('is-on', on);
    var icon = b.querySelector('.ccml-save__icon');
    var label = b.querySelector('.ccml-save__label');
    if (icon) icon.innerHTML = on ? TICK : ICON;
    if (label) label.textContent = on ? 'On My List' : 'Save to My List';
    b.setAttribute('title', on ? 'Remove from My List' : 'Save to My List');
  }

  function decorateCards(scope) {
    var cards = (scope || document).querySelectorAll('.product-card');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      if (card.getAttribute('data-ccml-done')) continue;
      var link = card.querySelector('a.product-card__title');
      var handle = handleOf(link && link.getAttribute('href'));
      if (!handle) continue;
      var slot = card.querySelector('.product-card__content') || card;
      card.setAttribute('data-ccml-done', '1');
      slot.appendChild(makeButton(handle, null, false));
    }
  }

  function productVariant(form) {
    if (!form) return null;
    var input = form.querySelector('[name="id"]');
    if (input && input.value) return input.value;
    try {
      var p = new URLSearchParams(window.location.search).get('variant');
      if (p) return p;
    } catch (e) {
      /* no-op */
    }
    return null;
  }

  function decorateProduct() {
    var wrap = document.querySelector('.product-form-wrapper');
    if (!wrap || wrap.getAttribute('data-ccml-done')) return;
    var handle = handleOf(window.location.pathname);
    if (!handle) return;
    var form = wrap.querySelector('form.product-form') || wrap.querySelector('form');
    wrap.setAttribute('data-ccml-done', '1');
    var b = makeButton(handle, productVariant(form), true);
    var holder = el('div', 'ccml-save-row');
    holder.appendChild(b);
    holder.appendChild(el('span', 'ccml-save-row__note',
      'Saves to this browser. Send it on from My List.'));
    wrap.appendChild(holder);

    /* Keep the saved variant in step with the picker. Concept rewrites the
       hidden id input when a variant changes rather than firing an event we
       can rely on, so the input is watched directly. */
    if (form) {
      var input = form.querySelector('[name="id"]');
      if (input && window.MutationObserver) {
        new window.MutationObserver(function () {
          var v = input.value;
          if (!v) return;
          b.setAttribute('data-ccml-variant', v);
          if (has(handle)) patch(handle, { id: String(v) });
        }).observe(input, { attributes: true, attributeFilter: ['value'] });
      }
      form.addEventListener('change', function () {
        var v = productVariant(form);
        if (!v) return;
        b.setAttribute('data-ccml-variant', v);
        if (has(handle)) patch(handle, { id: String(v) });
      });
    }
  }

  /* --------------------------------------------------------- header entry */

  function decorateHeader() {
    var carts = document.querySelectorAll('.header__buttons a.cart-drawer-button');
    for (var i = 0; i < carts.length; i++) {
      var cart = carts[i];
      if (cart.getAttribute('data-ccml-done')) continue;
      cart.setAttribute('data-ccml-done', '1');
      var a = el('a', 'ccml-head');
      a.href = root() + 'pages/my-list';
      a.innerHTML = ICON;
      var sr = el('span', 'ccml-head__sr', 'My List');
      a.appendChild(sr);
      var n = el('span', 'ccml-head__count');
      n.setAttribute('aria-hidden', 'true');
      a.appendChild(n);
      cart.parentNode.insertBefore(a, cart);
    }
  }

  /* Writing textContent replaces the text node even when the string is
     identical, and that is a childList mutation, and the observer below
     watches childList. Every write of a count goes through here, or the
     header would repaint itself forever at four frames a second. */
  function setText(node, text) {
    if (node.textContent !== text) node.textContent = text;
  }

  function paintCount() {
    var n = read().length;
    var pills = document.querySelectorAll('.ccml-head__count');
    for (var i = 0; i < pills.length; i++) {
      setText(pills[i], n ? String(n) : '');
      pills[i].classList.toggle('is-on', n > 0);
    }
    var srs = document.querySelectorAll('.ccml-head__sr');
    for (var j = 0; j < srs.length; j++) {
      setText(srs[j], n === 0 ? 'My List, empty'
        : (n === 1 ? 'My List, 1 item' : 'My List, ' + n + ' items'));
    }
    var counters = document.querySelectorAll('[data-ccml-count]');
    for (var k = 0; k < counters.length; k++) {
      setText(counters[k], String(n));
    }
  }

  function repaintAll() {
    var buttons = document.querySelectorAll('[data-ccml-save]');
    for (var i = 0; i < buttons.length; i++) paint(buttons[i]);
    paintCount();
  }

  /* ------------------------------------------------------------- the page */

  var cache = {};

  function fetchProduct(handle) {
    if (cache[handle]) return cache[handle];
    cache[handle] = window.fetch(root() + 'products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' }
    }).then(function (r) {
      if (!r.ok) throw new Error('gone');
      return r.json();
    }).catch(function () {
      return null;
    });
    return cache[handle];
  }

  function chosen(product, item) {
    var variants = (product && product.variants) || [];
    var i;
    if (item.id) {
      for (i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(item.id)) return variants[i];
      }
    }
    for (i = 0; i < variants.length; i++) {
      if (variants[i].available) return variants[i];
    }
    return variants[0] || null;
  }

  function permalink(rows) {
    var parts = [];
    for (var i = 0; i < rows.length; i++) {
      if (rows[i].variant) parts.push(rows[i].variant.id + ':' + rows[i].item.q);
    }
    if (!parts.length) return '';
    return window.location.origin + root() + 'cart/' + parts.join(',');
  }

  function asText(rows) {
    var lines = [];
    for (var i = 0; i < rows.length; i++) {
      var r = rows[i];
      if (!r.product) continue;
      var name = r.product.title;
      if (r.variant && r.variant.title && r.variant.title !== 'Default Title') {
        name += ' (' + r.variant.title + ')';
      }
      var sku = r.variant && r.variant.sku ? ' [SKU ' + r.variant.sku + ']' : '';
      lines.push(r.item.q + ' x ' + name + sku);
    }
    return lines.join(NL);
  }

  var renderToken = 0;

  function renderPage() {
    var mount = document.getElementById('ccml-root');
    if (!mount) return;
    /* Every edit to the list re-renders the page, and the render is async.
       Without a token, a quantity change that lands while an earlier render
       is still fetching paints the older list over the newer one. */
    renderToken += 1;
    var mine = renderToken;
    var items = read();

    if (!items.length) {
      mount.innerHTML = '';
      var empty = el('div', 'ccml-empty');
      empty.appendChild(el('h2', null, 'Your list is empty.'));
      empty.appendChild(el('p', null,
        'Browse the catalog and press Save to My List on anything you are '
        + 'considering. Nothing is ordered and nothing is held -- a list is '
        + 'just a list.'));
      var go = el('a', 'ccml-btn ccml-btn--solid', 'Browse the catalog');
      go.href = root() + 'collections';
      empty.appendChild(go);
      mount.appendChild(empty);
      return;
    }

    mount.innerHTML = '';
    mount.appendChild(el('p', 'ccml-loading', 'Loading your list...'));

    var jobs = items.map(function (item) {
      return fetchProduct(item.h).then(function (product) {
        return { item: item, product: product, variant: chosen(product, item) };
      });
    });

    window.Promise.all(jobs).then(function (rows) {
      if (mine !== renderToken) return;
      mount.innerHTML = '';
      var table = el('div', 'ccml-list');
      var total = 0;
      var live_rows = [];

      rows.forEach(function (row) {
        var card = el('div', 'ccml-row');
        if (!row.product) {
          card.classList.add('ccml-row--gone');
          card.appendChild(el('div', 'ccml-row__body',
            'This product is no longer available.'));
          var rm = el('button', 'ccml-link', 'Remove');
          rm.type = 'button';
          rm.setAttribute('aria-label',
            'Remove an item that is no longer available from My List');
          rm.addEventListener('click', function () {
            drop(row.item.h);
          });
          card.appendChild(rm);
          table.appendChild(card);
          return;
        }
        live_rows.push(row);

        var media = el('a', 'ccml-row__media');
        media.href = root() + 'products/' + row.product.handle;
        /* The thumbnail is the SAME destination as the title beside it, and
           it holds only a decorative image -- with no product photograph yet
           it holds nothing at all. axe called that what it is: a link with no
           accessible name. It is hidden from the accessibility tree and taken
           out of the tab order, which is what Concept itself does with the
           media link in product-card.liquid. */
        media.setAttribute('aria-hidden', 'true');
        media.setAttribute('tabindex', '-1');
        if (row.product.featured_image) {
          var img = document.createElement('img');
          /* featured_image is the FULL original -- 1200px for a 96px square.
             A list can hold sixty rows, so the CDN is asked for the size
             actually being painted. */
          var src = row.product.featured_image;
          img.src = src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=240';
          img.alt = '';
          img.loading = 'lazy';
          img.width = 120;
          img.height = 120;
          media.appendChild(img);
        }
        card.appendChild(media);

        var body = el('div', 'ccml-row__body');
        var title = el('a', 'ccml-row__title', row.product.title);
        title.href = root() + 'products/' + row.product.handle;
        body.appendChild(title);

        if (row.variant && row.variant.title && row.variant.title !== 'Default Title') {
          if (row.product.variants.length > 1) {
            var pick = el('select', 'ccml-row__variant');
            pick.setAttribute('aria-label', 'Option for ' + row.product.title);
            row.product.variants.forEach(function (v) {
              var o = document.createElement('option');
              o.value = v.id;
              o.textContent = v.title + (v.available ? '' : ' (unavailable)');
              if (String(v.id) === String(row.variant.id)) o.selected = true;
              pick.appendChild(o);
            });
            pick.addEventListener('change', function () {
              patch(row.item.h, { id: pick.value });
            });
            body.appendChild(pick);
          } else {
            body.appendChild(el('p', 'ccml-row__meta', row.variant.title));
          }
        }

        if (row.variant && row.variant.sku) {
          body.appendChild(el('p', 'ccml-row__sku', 'SKU ' + row.variant.sku));
        }
        if (row.variant && !row.variant.available) {
          body.appendChild(el('p', 'ccml-row__flag', 'Currently unavailable'));
        }
        card.appendChild(body);

        var qtyBox = el('div', 'ccml-row__qty');
        var qLabel = el('label', 'ccml-row__qtylabel', 'Qty');
        var qId = 'ccml-q-' + row.product.handle;
        qLabel.setAttribute('for', qId);
        var qty = document.createElement('input');
        qty.type = 'number';
        qty.min = '1';
        qty.max = '999';
        qty.step = '1';
        qty.id = qId;
        qty.value = String(row.item.q);
        qty.className = 'ccml-row__qtyinput';
        qty.addEventListener('change', function () {
          var v = parseInt(qty.value, 10);
          if (!(v > 0)) v = 1;
          if (v > 999) v = 999;
          qty.value = String(v);
          patch(row.item.h, { q: v });
        });
        qtyBox.appendChild(qLabel);
        qtyBox.appendChild(qty);
        card.appendChild(qtyBox);

        var priceBox = el('div', 'ccml-row__price');
        if (row.variant) {
          priceBox.appendChild(el('span', 'ccml-row__each', money(row.variant.price) + ' each'));
          priceBox.appendChild(el('strong', null, money(row.variant.price * row.item.q)));
          total += row.variant.price * row.item.q;
        }
        card.appendChild(priceBox);

        var remove = el('button', 'ccml-row__remove', 'Remove');
        remove.type = 'button';
        remove.setAttribute('aria-label', 'Remove ' + row.product.title + ' from My List');
        remove.addEventListener('click', function () {
          drop(row.item.h);
          say(row.product.title + ' removed from My List.');
        });
        card.appendChild(remove);

        table.appendChild(card);
      });

      mount.appendChild(table);

      var foot = el('div', 'ccml-total');
      foot.appendChild(el('span', null, 'Estimated total'));
      foot.appendChild(el('strong', null, money(total)));
      mount.appendChild(foot);
      mount.appendChild(el('p', 'ccml-fine',
        'Prices are read from the store each time this page loads, so what you '
        + 'see here is current. Shipping and tax are not included, and nothing '
        + 'on this list is reserved.'));

      mount.appendChild(buildActions(live_rows));
      paintCount();
    }).catch(function () {
      if (mine !== renderToken) return;
      mount.innerHTML = '';
      mount.appendChild(el('p', 'ccml-fine',
        'Something went wrong loading your list. Refreshing the page usually '
        + 'fixes it, and nothing has been lost.'));
    });
  }

  function buildActions(rows) {
    var box = el('div', 'ccml-acts');

    var cart = el('button', 'ccml-btn ccml-btn--solid', 'Add everything to cart');
    cart.type = 'button';
    cart.addEventListener('click', function () {
      var payload = [];
      rows.forEach(function (r) {
        if (r.variant && r.variant.available) {
          payload.push({ id: r.variant.id, quantity: r.item.q });
        }
      });
      if (!payload.length) {
        say('Nothing on the list is currently available to add.');
        return;
      }
      cart.disabled = true;
      cart.textContent = 'Adding...';
      window.fetch(root() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: payload })
      }).then(function (r) {
        if (!r.ok) throw new Error('no');
        window.location.href = root() + 'cart';
      }).catch(function () {
        cart.disabled = false;
        cart.textContent = 'Add everything to cart';
        say('Could not add the list to the cart. Please try again.');
      });
    });
    box.appendChild(cart);

    var link = permalink(rows);
    if (link) {
      var copy = el('button', 'ccml-btn ccml-btn--ghost', 'Copy a link to this list');
      copy.type = 'button';
      copy.addEventListener('click', function () {
        var done = function () {
          copy.textContent = 'Link copied';
          say('A link to your list has been copied to the clipboard.');
          window.setTimeout(function () {
            copy.textContent = 'Copy a link to this list';
          }, 2600);
        };
        if (window.navigator.clipboard && window.navigator.clipboard.writeText) {
          window.navigator.clipboard.writeText(link).then(done).catch(function () {
            window.prompt('Copy this link:', link);
          });
        } else {
          window.prompt('Copy this link:', link);
        }
      });
      box.appendChild(copy);

      var mail = el('a', 'ccml-btn ccml-btn--ghost', 'Email this list');
      mail.href = 'mailto:?subject=' + encodeURIComponent('Supply list from Central Creativity')
        + '&body=' + encodeURIComponent(
          'Here is the list:' + NL + NL + asText(rows)
          + NL + NL + 'Open this link to load the whole list into a cart:' + NL + link
          + NL + NL + 'Central Creativity, 866-381-1760');
      box.appendChild(mail);
    }

    var quote = el('a', 'ccml-btn ccml-btn--ghost', 'Request a quote for these items');
    quote.href = root() + 'pages/quote?ccml=' + encodeURIComponent(asText(rows));
    box.appendChild(quote);

    var print = el('button', 'ccml-btn ccml-btn--ghost', 'Print this list');
    print.type = 'button';
    print.addEventListener('click', function () {
      window.print();
    });
    box.appendChild(print);

    var clear = el('button', 'ccml-link ccml-clear', 'Clear the whole list');
    clear.type = 'button';
    clear.addEventListener('click', function () {
      if (window.confirm('Remove everything from My List? This cannot be undone.')) {
        write([]);
        say('My List is now empty.');
      }
    });
    box.appendChild(clear);

    return box;
  }

  /* --------------------------------------------- carrying a list to quote */

  function prefillQuote() {
    var text = null;
    try {
      text = new URLSearchParams(window.location.search).get('ccml');
    } catch (e) {
      return;
    }
    if (!text) return;
    var area = document.querySelector('textarea');
    if (!area) return;
    if (area.value && area.value.trim()) return;
    area.value = 'Please quote the following:' + NL + NL + text + NL + NL;
    try {
      area.dispatchEvent(new Event('input', { bubbles: true }));
    } catch (e) {
      /* no-op */
    }
  }

  /* ------------------------------------------------------------------ boot */

  /* WHAT THE OBSERVER IS ACTUALLY DEFENDING AGAINST, stated accurately,
     because the first version of this comment stated something false.

     A collection page on the preview measured six product cards and zero save
     buttons, which looked exactly like the theme swapping <body> out from
     under a stale observer. It was not that. Checked properly -- a marker set
     on window does NOT survive a link click here -- every navigation in this
     theme is a real document load, and the zero was a deferred script that
     had simply not run yet when the measurement was taken. The bug was in the
     measurement.

     What is true, and does need handling: Concept rewrites the URL and
     re-renders the product grid in place when collection facets change, and
     quick view, carousels and the section rendering API all inject cards
     after load. So the observer looks for both -- new cards, and a URL that
     moved without a reload -- and it watches documentElement rather than
     body, which costs nothing and survives anything that ever does replace
     the body wholesale. */

  var seen_url = null;

  function here() {
    return window.location.pathname + window.location.search;
  }

  function sweep(fresh) {
    if (fresh) {
      /* The live region went out with the old body. */
      live = null;
    }
    decorateHeader();
    decorateCards(document);
    decorateProduct();
    if (fresh) {
      prefillQuote();
      renderPage();
    }
    paintCount();
  }

  function boot() {
    /* The theme may also re-execute this file after a transition. Decorate
       again either way, but do not stack a second observer and a second
       listener on every navigation. */
    var again = window.__ccmlBooted === true;
    window.__ccmlBooted = true;

    seen_url = here();
    try {
      sweep(true);
    } catch (e) {
      /* One broken page must not take the rest of the storefront with it. */
    }
    if (again) return;

    /* document survives a body swap, so this listener does too. */
    document.addEventListener(EV, function () {
      try {
        repaintAll();
        renderPage();
      } catch (e) {
        /* no-op */
      }
    });

    /* Cards also arrive late from carousels, quick view, filtered collection
       pages and Concept's section rendering API. */
    if (window.MutationObserver) {
      var pending = false;
      new window.MutationObserver(function () {
        if (pending) return;
        pending = true;
        window.setTimeout(function () {
          pending = false;
          try {
            var url = here();
            var moved = url !== seen_url;
            seen_url = url;
            sweep(moved);
          } catch (e) {
            /* no-op */
          }
        }, 120);
      }).observe(document.documentElement, { childList: true, subtree: true });
    }

    /* Two tabs open on the same store should not disagree about the list. */
    window.addEventListener('storage', function (event) {
      if (event && event.key === KEY) {
        try {
          repaintAll();
          renderPage();
        } catch (e) {
          /* no-op */
        }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.CCMyList = {
    read: read,
    count: function () { return read().length; },
    add: add,
    remove: drop,
    clear: function () { write([]); }
  };
}());
